'use client'

import { MaximizeIcon, Minimize2Icon, SquareFunctionIcon } from 'lucide-react'
import { createElement, type ReactNode, useEffect, useRef, useState } from 'react'
import { createRoot, type Root } from 'react-dom/client'

import {
  CHART_ANALYTICS_BY_KEY,
  CHART_ANALYTICS_DEFINITIONS,
  getChartAnalyticsMetricByStudyName,
  getEffectiveStudyInputs,
  OI_OHLC_FIELDS,
} from '@/features/chart/lib/config/chart-analytics'
import {
  NATIVE_STUDIES_ACCESS_NAMES,
  SPECIAL_ANALYTICS_STUDY_NAMES,
} from '@/features/chart/lib/config/chart-indicator-catalog'
import {
  CHART_COLORS,
  getMainPaneVolumeScaleCeiling,
} from '@/features/chart/lib/constants/chart-colors'
import {
  LEVEL_PROFILE_MEASURE_INPUT_ID,
  LEVEL_PROFILE_STUDIES,
  type LevelProfileKind,
} from '@/features/chart/lib/level-profile-studies'
import {
  DEFAULT_ORDERBOOK_HEATMAP_GROUPING,
  DEFAULT_ORDERBOOK_HEATMAP_RANGE,
  formatOrderbookHeatmapGrouping,
  ORDERBOOK_HEATMAP_GROUPING_INPUT_ID,
  ORDERBOOK_HEATMAP_GROUPING_OPTIONS,
  ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID,
  ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID,
  ORDERBOOK_HEATMAP_RANGE_INPUT_ID,
  ORDERBOOK_HEATMAP_RANGE_OPTIONS,
  ORDERBOOK_HEATMAP_STUDY,
} from '@/features/chart/lib/orderbook-heatmap'
import {
  DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS,
  VISIBLE_RANGE_PROFILE_ROW_SIZE_INPUT_ID,
  VISIBLE_RANGE_PROFILE_ROWS_LAYOUT_INPUT_ID,
  VISIBLE_RANGE_PROFILE_STUDIES,
  VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID,
  VISIBLE_RANGE_PROFILE_VALUE_AREA_INPUT_ID,
  VISIBLE_RANGE_PROFILE_VOLUME_MODE_INPUT_ID,
  type VisibleRangeProfileKind,
} from '@/features/chart/lib/visible-range-profile-studies'
import { getNumberFormatter } from '@/hooks/use-number-format'
import type { AppLocale } from '@/i18n/config'
import { getChartTimeframeInfo } from '@/lib/chart-timeframe'
import { localizeSeparators, SEPARATOR_STYLES } from '@/lib/number-format'
import {
  OrderXBrokerBridge,
  type OrderXTradingOrderIntent,
} from '@/lib/tradingview/broker/orderx-broker-bridge'
import type { CvdMeasure } from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  ChartDatafeed,
  chartResolution,
  normalizeBarTimeSec,
} from '@/lib/tradingview/datafeeds/chart-datafeed'
import { buildStudyInputs, readStudyInputValues } from '@/lib/tradingview/study-inputs'
import { TVWidgetInstance } from '@/lib/tradingview/types/tv-widget.types'
import { isEditableTarget } from '@/lib/utils/is-editable-target'

type AnalyticsMetricKey = (typeof CHART_ANALYTICS_DEFINITIONS)[number]['key']
type AnalyticsStudyIdMap = Partial<Record<AnalyticsMetricKey, string>>

const ZERO_CENTERED_NEGATIVE_COLOR = CHART_COLORS.loss
const ZERO_CENTERED_POSITIVE_COLOR = CHART_COLORS.profit
const LIQUIDATION_BUY_COLOR = CHART_COLORS.profit
const LIQUIDATION_SELL_COLOR = CHART_COLORS.loss
const TV_TRANSPARENT_BACKGROUND = 'rgba(0, 0, 0, 0)'
const TV_LIGHT_AXIS_HIGHLIGHT = '#ebebeb'
const TV_LIGHT_PRIMARY_TEXT = '#0f0f0f'
const FUNDING_RATE_PRECISION = 4
const OPEN_INTEREST_PRECISION = 4
const patchedAxisCanvasPrototypes = new WeakSet<object>()

type ProfileControlStudyTextInput = {
  id: string
  name: string
  type: 'text'
  defval: string
  options: string[]
  optionsTitles: Record<string, string>
}
type ProfileControlStudyBoolInput = {
  id: string
  name: string
  type: 'bool'
  defval: boolean
}
type ProfileControlStudyIntegerInput = {
  id: string
  name: string
  type: 'integer'
  defval: number
  min?: number
  max?: number
}
// Free text (no options): the heatmap size inputs take shorthand like "500k" / "1m".
type ProfileControlStudyFreeTextInput = {
  id: string
  name: string
  type: 'text'
  defval: string
}
type ProfileControlStudyInput =
  | ProfileControlStudyTextInput
  | ProfileControlStudyBoolInput
  | ProfileControlStudyIntegerInput
  | ProfileControlStudyFreeTextInput

const VISIBLE_RANGE_PROFILE_UNIT_INPUT = {
  id: VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID,
  name: 'Unit',
  type: 'text' as const,
  defval: 'quote',
  options: ['base', 'quote'],
  optionsTitles: {
    base: 'Base',
    quote: 'Quote',
  },
}

// Shared across every profile family — the per-bucket "S 6.91M / L 0"-style text overlay reads
// as clutter once several buckets are non-zero at once (see oems #261 follow-up), so each
// indicator gets its own on/off toggle in its native TV settings dialog, same discovery path as
// the unit/measure input above.
export const PROFILE_SHOW_LABELS_INPUT_ID = 'showLabels'
const PROFILE_SHOW_LABELS_INPUT = {
  id: PROFILE_SHOW_LABELS_INPUT_ID,
  name: 'Show Labels',
  type: 'bool' as const,
  defval: true,
}

const VISIBLE_RANGE_PROFILE_INPUTS: ProfileControlStudyInput[] = [
  VISIBLE_RANGE_PROFILE_UNIT_INPUT,
  {
    id: VISIBLE_RANGE_PROFILE_ROWS_LAYOUT_INPUT_ID,
    name: 'Rows Layout',
    type: 'text' as const,
    defval: DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS.rowsLayout,
    options: ['numberOfRows', 'ticksPerRow'],
    optionsTitles: {
      numberOfRows: 'Number Of Rows',
      ticksPerRow: 'Ticks Per Row',
    },
  },
  {
    id: VISIBLE_RANGE_PROFILE_ROW_SIZE_INPUT_ID,
    name: 'Row Size',
    type: 'integer' as const,
    defval: DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS.rowSize,
    min: 1,
    max: 500,
  },
  {
    id: VISIBLE_RANGE_PROFILE_VOLUME_MODE_INPUT_ID,
    name: 'Volume',
    type: 'text' as const,
    defval: DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS.volumeMode,
    options: ['upDown', 'total', 'delta'],
    optionsTitles: { upDown: 'Up/Down', total: 'Total', delta: 'Delta' },
  },
  {
    id: VISIBLE_RANGE_PROFILE_VALUE_AREA_INPUT_ID,
    name: 'Value Area Volume',
    type: 'integer' as const,
    defval: DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS.valueAreaPercent,
    min: 0,
    max: 100,
  },
  PROFILE_SHOW_LABELS_INPUT,
]

const VISIBLE_RANGE_PROFILE_STUDY_ID_LABELS: Record<VisibleRangeProfileKind, string> = {
  volume: 'Volume',
  liquidation: 'Liquidation',
  openInterest: 'OpenInterest',
}

const LEVEL_PROFILE_MEASURE_INPUT = {
  id: LEVEL_PROFILE_MEASURE_INPUT_ID,
  name: 'Measure',
  type: 'text' as const,
  defval: 'notional',
  options: ['notional', 'count'],
  optionsTitles: {
    notional: 'Notional',
    count: 'Count',
  },
}

const LEVEL_PROFILE_STUDY_ID_LABELS: Record<LevelProfileKind, string> = {
  liquidationLevels: 'LiquidationLevels',
  stopLevels: 'StopLevels',
}

// Shared by both profile families (Volume/Liquidation/OI visible-range, and Hyperliquid
// liquidation/stop levels) — each family gets its own registry, id-label map, input definitions
// (unit/measure + the shared show-labels toggle), and id prefix/suffix so the two families'
// generated TV study ids can never collide, even though the dummy-study-for-legend-persistence
// shape is otherwise identical.
function createProfileControlStudies<TKind extends string>(
  registry: Record<TKind, { name: string; shortDescription: string }>,
  idLabels: Record<TKind, string>,
  inputs: ProfileControlStudyInput[],
  idPrefix: string,
  idSuffix: string
) {
  return Object.entries(registry).map(([kind, definition]) => {
    const { name, shortDescription } = definition as { name: string; shortDescription: string }
    return {
      name,
      metainfo: {
        _metainfoVersion: 53,
        id: `${idPrefix}${idLabels[kind as TKind]}${idSuffix}`,
        description: name,
        shortDescription,
        is_price_study: true,
        isCustomIndicator: true,
        linkedToSeries: true,
        format: { type: 'volume', precision: 2 },
        plots: [{ id: 'plot_0', type: 'line' }],
        defaults: {
          styles: {
            plot_0: {
              linestyle: 0,
              linewidth: 1,
              plottype: 0,
              trackPrice: false,
              transparency: 100,
              visible: true,
              color: TV_TRANSPARENT_BACKGROUND,
              display: 0,
            },
          },
          precision: 2,
          inputs: Object.fromEntries(inputs.map((input) => [input.id, input.defval])),
        },
        styles: {
          plot_0: { title: '' },
        },
        inputs,
      },
      constructor: function (this: {
        main?: (
          context: unknown,
          inputCallback: (index: number) => string | number | boolean | undefined
        ) => number[]
      }) {
        this.main = function (_context, inputCallback) {
          inputs.forEach((_, index) => inputCallback(index))
          return [NaN]
        }
      },
    }
  })
}

function createVisibleRangeProfileControlStudies() {
  return createProfileControlStudies(
    VISIBLE_RANGE_PROFILE_STUDIES,
    VISIBLE_RANGE_PROFILE_STUDY_ID_LABELS,
    VISIBLE_RANGE_PROFILE_INPUTS,
    'OrderXVisibleRange',
    'Profile@tv-basicstudies-1'
  )
}

function createLevelProfileControlStudies() {
  return createProfileControlStudies(
    LEVEL_PROFILE_STUDIES,
    LEVEL_PROFILE_STUDY_ID_LABELS,
    [LEVEL_PROFILE_MEASURE_INPUT, PROFILE_SHOW_LABELS_INPUT],
    'OrderXLevelProfile',
    '@tv-basicstudies-1'
  )
}

// Same band as the Percept heatmap's input; tv-chart-widget mirrors it into chartOption.
const ORDERBOOK_HEATMAP_RANGE_INPUT = {
  id: ORDERBOOK_HEATMAP_RANGE_INPUT_ID,
  name: 'Price Range',
  type: 'text' as const,
  defval: String(DEFAULT_ORDERBOOK_HEATMAP_RANGE),
  options: ORDERBOOK_HEATMAP_RANGE_OPTIONS.map(String),
  optionsTitles: Object.fromEntries(
    ORDERBOOK_HEATMAP_RANGE_OPTIONS.map((range) => [String(range), `±${range}% of mid`])
  ),
}

// Price grouping, as the order book widget groups rows; tv-chart-widget mirrors it into chartOption.
const ORDERBOOK_HEATMAP_GROUPING_INPUT = {
  id: ORDERBOOK_HEATMAP_GROUPING_INPUT_ID,
  name: 'Price Grouping',
  type: 'text' as const,
  defval: String(DEFAULT_ORDERBOOK_HEATMAP_GROUPING),
  options: ORDERBOOK_HEATMAP_GROUPING_OPTIONS.map(String),
  optionsTitles: Object.fromEntries(
    ORDERBOOK_HEATMAP_GROUPING_OPTIONS.map((grouping) => [
      String(grouping),
      formatOrderbookHeatmapGrouping(grouping),
    ])
  ),
}

// Size filter: only levels resting between Min and Max are drawn (0 = no bound). The hover card
// still lists every level. Shared with the Percept control via chartOption.
const ORDERBOOK_HEATMAP_SIZE_INPUTS = [
  {
    id: ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID,
    name: 'Min Size (e.g. 500k)',
    type: 'text' as const,
    defval: '',
  },
  {
    id: ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID,
    name: 'Max Size (e.g. 5m)',
    type: 'text' as const,
    defval: '',
  },
]

function createOrderbookHeatmapControlStudies() {
  return createProfileControlStudies(
    { orderbookHeatmap: ORDERBOOK_HEATMAP_STUDY },
    { orderbookHeatmap: 'OrderbookHeatmap' },
    [
      ORDERBOOK_HEATMAP_RANGE_INPUT,
      ORDERBOOK_HEATMAP_GROUPING_INPUT,
      ...ORDERBOOK_HEATMAP_SIZE_INPUTS,
    ],
    'OrderX',
    '@tv-basicstudies-1'
  )
}

function isFundingRateMetric(metric: string) {
  return (
    metric === 'fundingRate' ||
    metric === 'aggregatedFundingRateAvgClose' ||
    metric === 'fundingApr'
  )
}

function isOpenInterestMetric(metric: string) {
  return (
    metric === 'openInterestBase' ||
    metric === 'openInterestQuote' ||
    metric === 'aggregatedOpenInterest'
  )
}

function getStudyPrecision(metric: string, unit?: string) {
  if (unit === 'count') return 0
  if (isFundingRateMetric(metric)) return FUNDING_RATE_PRECISION
  if (isOpenInterestMetric(metric)) return OPEN_INTEREST_PRECISION
  if (metric === 'deltaPercentage' || metric === 'parkinsonVolatility') return 2
  return 2
}

function trimFormattedNumber(value: string) {
  return value.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
}

function formatCompactVolumeValue(value: number, maxPrecision = 4) {
  if (!Number.isFinite(value)) return ''

  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const units = [
    { threshold: 1e12, suffix: 'T' },
    { threshold: 1e9, suffix: 'B' },
    { threshold: 1e6, suffix: 'M' },
    { threshold: 1e3, suffix: 'K' },
  ]
  const unit = units.find((item) => absValue >= item.threshold)

  if (!unit) {
    const precision = absValue >= 100 ? 0 : absValue >= 10 ? 1 : Math.min(maxPrecision, 4)
    return `${sign}${trimFormattedNumber(absValue.toFixed(precision))}`
  }

  const scaledValue = absValue / unit.threshold
  const precision =
    scaledValue >= 100
      ? 0
      : scaledValue >= 10
        ? Math.min(maxPrecision, 1)
        : Math.min(maxPrecision, 2)

  return `${sign}${trimFormattedNumber(scaledValue.toFixed(precision))}${unit.suffix}`
}

function formatPercentValue(value: number, maxPrecision = 4) {
  if (!Number.isFinite(value)) return ''
  return `${trimFormattedNumber(value.toFixed(maxPrecision))}%`
}

function normalizeAnalyticsStudyValue(metric: string, rawValue: number | null | undefined) {
  if (rawValue === null || rawValue === undefined) return rawValue

  if (
    metric === 'deltaPercentage' ||
    metric === 'parkinsonVolatility' ||
    isFundingRateMetric(metric)
  ) {
    return rawValue * 100
  }

  if (metric === 'vwap' && Math.abs(rawValue) < 1e-9) {
    return null
  }

  if (metric === 'liqwap' && (Math.abs(rawValue) < 1e-9 || Math.abs(rawValue - 1) < 1e-9)) {
    return null
  }

  return rawValue
}

function getOpenInterestScaleBounds(open: number, high: number, low: number, close: number) {
  const highValue = Math.max(open, high, low, close)
  const lowValue = Math.min(open, high, low, close)
  const range = highValue - lowValue
  const fallbackStep = Math.max(Math.abs(close) * 0.02, 1e-9)
  const step = Math.max(range, fallbackStep)

  return {
    upper: highValue + step * 2,
    lower: Math.max(0, lowValue - step * 2),
  }
}

const TV_BACKGROUND_OVERRIDES = {
  'layout.backgroundColor': TV_TRANSPARENT_BACKGROUND,
  'paneProperties.background': TV_TRANSPARENT_BACKGROUND,
  'paneProperties.backgroundType': 'solid',
  'paneProperties.backgroundGradientStartColor': TV_TRANSPARENT_BACKGROUND,
  'paneProperties.backgroundGradientEndColor': TV_TRANSPARENT_BACKGROUND,
  'paneProperties.vertGridProperties.color': TV_TRANSPARENT_BACKGROUND,
  'paneProperties.horzGridProperties.color': TV_TRANSPARENT_BACKGROUND,
} as const

const TV_MAIN_SERIES_OVERRIDES = {
  'mainSeriesProperties.candleStyle.upColor': CHART_COLORS.profit,
  'mainSeriesProperties.candleStyle.downColor': CHART_COLORS.loss,
  'mainSeriesProperties.candleStyle.borderUpColor': CHART_COLORS.profit,
  'mainSeriesProperties.candleStyle.borderDownColor': CHART_COLORS.loss,
  'mainSeriesProperties.candleStyle.wickUpColor': CHART_COLORS.profit,
  'mainSeriesProperties.candleStyle.wickDownColor': CHART_COLORS.loss,
  'mainSeriesProperties.hollowCandleStyle.upColor': CHART_COLORS.profit,
  'mainSeriesProperties.hollowCandleStyle.downColor': CHART_COLORS.loss,
  'mainSeriesProperties.hollowCandleStyle.borderUpColor': CHART_COLORS.profit,
  'mainSeriesProperties.hollowCandleStyle.borderDownColor': CHART_COLORS.loss,
  'mainSeriesProperties.hollowCandleStyle.wickUpColor': CHART_COLORS.profit,
  'mainSeriesProperties.hollowCandleStyle.wickDownColor': CHART_COLORS.loss,
  'mainSeriesProperties.haStyle.upColor': CHART_COLORS.profit,
  'mainSeriesProperties.haStyle.downColor': CHART_COLORS.loss,
  'mainSeriesProperties.haStyle.borderUpColor': CHART_COLORS.profit,
  'mainSeriesProperties.haStyle.borderDownColor': CHART_COLORS.loss,
  'mainSeriesProperties.haStyle.wickUpColor': CHART_COLORS.profit,
  'mainSeriesProperties.haStyle.wickDownColor': CHART_COLORS.loss,
} as const

const TV_CHART_OVERRIDES = {
  ...TV_BACKGROUND_OVERRIDES,
  ...TV_MAIN_SERIES_OVERRIDES,
  'mainSeriesProperties.showCountdown': true,
  'scalesProperties.lineColor': CHART_COLORS.transparent,
  'paneProperties.legendProperties.showStudyArguments': false,
} as const

type TVColorTheme = 'light' | 'dark'

// TV's LanguageCode union has no 'zh-CN' — simplified Chinese is just 'zh'
// there ('zh_TW' is traditional). The other app locales pass through as-is.
const APP_LOCALE_TO_TV_LOCALE: Record<AppLocale, string> = {
  en: 'en',
  'zh-CN': 'zh',
  de: 'de',
  ko: 'ko',
}

function resolveTVLocale(locale: AppLocale): string {
  return APP_LOCALE_TO_TV_LOCALE[locale] ?? 'en'
}

function getTVChartOverrides(colorTheme: TVColorTheme) {
  const dark = colorTheme === 'dark'
  const seriesLine = dark ? CHART_COLORS.darkSeriesLine : CHART_COLORS.seriesLine
  return {
    ...TV_CHART_OVERRIDES,
    'scalesProperties.textColor': dark ? CHART_COLORS.darkScaleText : CHART_COLORS.scaleText,
    'mainSeriesProperties.areaStyle.linecolor': seriesLine,
    'mainSeriesProperties.areaStyle.linewidth': 2,
    'mainSeriesProperties.areaStyle.color1': dark
      ? CHART_COLORS.darkSeriesFillTop
      : CHART_COLORS.seriesFillTop,
    'mainSeriesProperties.areaStyle.color2': dark
      ? CHART_COLORS.darkSeriesFillBottom
      : CHART_COLORS.seriesFillBottom,
    'mainSeriesProperties.lineStyle.color': seriesLine,
  }
}

function applyTVClientStyle(widget: TVWidgetInstance, colorTheme: TVColorTheme) {
  const overrides = getTVChartOverrides(colorTheme)
  widget.applyOverrides(overrides)
  widget.activeChart().applyOverrides(overrides)
  widget.applyTradingCustomization?.(TV_TRADING_CUSTOMIZATION)
}

function renderFullscreenHeaderButton(root: Root, isFullscreen: boolean, onClick: () => void) {
  const label = isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
  root.render(
    createElement(
      'button',
      {
        type: 'button',
        className: 'orderx-tv-header-button orderx-tv-header-button--icon-only',
        title: label,
        'aria-label': label,
        onClick,
      },
      createElement(isFullscreen ? Minimize2Icon : MaximizeIcon, {
        size: 18,
        'aria-hidden': true,
      })
    )
  )
}

const TV_TRADING_CUSTOMIZATION = {
  brokerOrder: {
    'buy.normal.text.backgroundColor': CHART_COLORS.profit,
    'buy.normal.text.buttonTextColor': '#ffffff',
    'buy.normal.text.textColor': '#ffffff',
    'buy.normal.text.dividerColor': 'rgba(255, 255, 255, 0.28)',
    'buy.normal.text.activeColor': '#ffffff',
    'buy.normal.borderBackgroundColor': 'rgba(0, 182, 143, 0.16)',
    'buy.normal.borderColor': CHART_COLORS.profit,
    'buy.normal.lineColor': CHART_COLORS.profit,
    'buy.normal.labelTickColor': CHART_COLORS.profit,
    'sell.normal.text.backgroundColor': CHART_COLORS.loss,
    'sell.normal.text.buttonTextColor': '#ffffff',
    'sell.normal.text.textColor': '#ffffff',
    'sell.normal.text.dividerColor': 'rgba(255, 255, 255, 0.22)',
    'sell.normal.text.activeColor': '#ffffff',
    'sell.normal.borderBackgroundColor': 'rgba(223, 72, 76, 0.16)',
    'sell.normal.borderColor': CHART_COLORS.loss,
    'sell.normal.lineColor': CHART_COLORS.loss,
    'sell.normal.labelTickColor': CHART_COLORS.loss,
  },
} as const

const LIQUIDATION_SPLIT_INPUT_SPECS = getEffectiveStudyInputs(CHART_ANALYTICS_BY_KEY.buySellLiq)
const LIQUIDATION_SPLIT_STUDY_INPUTS = buildStudyInputs(LIQUIDATION_SPLIT_INPUT_SPECS)

function getBarCloseAtTime(
  datafeed: ChartDatafeed | null,
  time: number,
  context?: { symbol?: string; timeframeSeconds?: number }
): number | null {
  return datafeed?.getRawBarCloseValue(time, context) ?? null
}

export function sanitizeTVClientStyleState(
  value: unknown,
  colorTheme: TVColorTheme = 'light'
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeTVClientStyleState(item, colorTheme))
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  const next: Record<string, unknown> = {}

  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    next[key] = sanitizeTVClientStyleState(item, colorTheme)
  })

  Object.entries(TV_BACKGROUND_OVERRIDES).forEach(([key, item]) => {
    if (key in next) next[key] = item
  })

  if (next.layout && typeof next.layout === 'object' && !Array.isArray(next.layout)) {
    next.layout = {
      ...(next.layout as Record<string, unknown>),
      backgroundColor: TV_TRANSPARENT_BACKGROUND,
    }
  }

  if (
    next.paneProperties &&
    typeof next.paneProperties === 'object' &&
    !Array.isArray(next.paneProperties)
  ) {
    const paneProperties = next.paneProperties as Record<string, unknown>
    const savedLegendProperties = paneProperties.legendProperties
    next.paneProperties = {
      ...paneProperties,
      background: TV_TRANSPARENT_BACKGROUND,
      backgroundType: 'solid',
      backgroundGradientStartColor: TV_TRANSPARENT_BACKGROUND,
      backgroundGradientEndColor: TV_TRANSPARENT_BACKGROUND,
      legendProperties: {
        ...(savedLegendProperties &&
        typeof savedLegendProperties === 'object' &&
        !Array.isArray(savedLegendProperties)
          ? (savedLegendProperties as Record<string, unknown>)
          : {}),
        // Saved layouts may retain the old global `true`. Keep ordinary inputs such as
        // VWAP lookback hidden; denomination inputs opt back in via isAlwaysShownInLegend.
        showStudyArguments: false,
      },
    }
  }

  if (
    next.mainSeriesProperties &&
    typeof next.mainSeriesProperties === 'object' &&
    !Array.isArray(next.mainSeriesProperties)
  ) {
    const mainSeriesProperties = next.mainSeriesProperties as Record<string, unknown>
    const styleOverrides = {
      candleStyle: {
        upColor: CHART_COLORS.profit,
        downColor: CHART_COLORS.loss,
        borderUpColor: CHART_COLORS.profit,
        borderDownColor: CHART_COLORS.loss,
        wickUpColor: CHART_COLORS.profit,
        wickDownColor: CHART_COLORS.loss,
      },
      hollowCandleStyle: {
        upColor: CHART_COLORS.profit,
        downColor: CHART_COLORS.loss,
        borderUpColor: CHART_COLORS.profit,
        borderDownColor: CHART_COLORS.loss,
        wickUpColor: CHART_COLORS.profit,
        wickDownColor: CHART_COLORS.loss,
      },
      haStyle: {
        upColor: CHART_COLORS.profit,
        downColor: CHART_COLORS.loss,
        borderUpColor: CHART_COLORS.profit,
        borderDownColor: CHART_COLORS.loss,
        wickUpColor: CHART_COLORS.profit,
        wickDownColor: CHART_COLORS.loss,
      },
    }

    Object.entries(styleOverrides).forEach(([styleKey, styleOverride]) => {
      const savedStyle = mainSeriesProperties[styleKey]
      mainSeriesProperties[styleKey] = {
        ...(savedStyle && typeof savedStyle === 'object' && !Array.isArray(savedStyle)
          ? (savedStyle as Record<string, unknown>)
          : {}),
        ...styleOverride,
      }
    })
  }

  if (
    next.scalesProperties &&
    typeof next.scalesProperties === 'object' &&
    !Array.isArray(next.scalesProperties)
  ) {
    next.scalesProperties = {
      ...(next.scalesProperties as Record<string, unknown>),
      lineColor: CHART_COLORS.transparent,
      textColor: colorTheme === 'dark' ? CHART_COLORS.darkScaleText : CHART_COLORS.scaleText,
    }
  }

  return next
}

function reconcileAnalyticsStudyIds(
  chart: ReturnType<TVWidgetInstance['activeChart']>
): AnalyticsStudyIdMap {
  const studies = chart.getAllStudies()
  const nextStudyIds: AnalyticsStudyIdMap = {}

  studies.forEach((study) => {
    for (const [metric, name] of Object.entries(SPECIAL_ANALYTICS_STUDY_NAMES)) {
      if (study.name === name) {
        nextStudyIds[metric as keyof typeof SPECIAL_ANALYTICS_STUDY_NAMES] = study.id
        return
      }
    }

    const metric = getChartAnalyticsMetricByStudyName(study.name)
    if (metric) {
      nextStudyIds[metric] = study.id
    }
  })

  return nextStudyIds
}

function warmUpPastHistory(widget: TVWidgetInstance, timeframeSeconds: number) {
  const chart = widget.activeChart()
  const range = chart.getVisibleRange()
  if (!Number.isFinite(range.from) || !Number.isFinite(range.to) || range.from >= range.to) return

  const lookbackSeconds = Math.max(timeframeSeconds * 120, 24 * 60 * 60)
  const warmupRange = {
    from: range.from - lookbackSeconds,
    to: range.to,
  }

  try {
    const result = chart.setVisibleRange(warmupRange)
    if (result && typeof result.then === 'function') {
      result.then(() => chart.setVisibleRange(range)).catch(() => {})
      return
    }

    window.setTimeout(() => {
      try {
        void chart.setVisibleRange(range)
      } catch {}
    }, 250)
  } catch {}
}

// ---------------------------------------------------------------------------
// Script loader singleton — prevents duplicate injection in React Strict Mode
// ---------------------------------------------------------------------------

let tvScriptPromise: Promise<void> | null = null
const TV_SCRIPT_ELEMENT_ID = 'tradingview-charting-library'

function ensureTVScript(): Promise<void> {
  if (typeof window !== 'undefined' && window.TradingView) return Promise.resolve()
  if (tvScriptPromise) return tvScriptPromise
  tvScriptPromise = new Promise<void>((resolve, reject) => {
    const libraryPath = process.env.NEXT_PUBLIC_TV_LIBRARY_PATH ?? '/charting_library/'
    const scriptUrl = `${libraryPath.replace(/\/$/, '')}/charting_library.standalone.js`
    const existingScript = document.getElementById(TV_SCRIPT_ELEMENT_ID)
    const script =
      existingScript instanceof HTMLScriptElement
        ? existingScript
        : document.createElement('script')

    const handleLoad = () => {
      cleanup()
      resolve()
    }
    const handleError = () => {
      cleanup()
      tvScriptPromise = null
      reject(new Error('Failed to load TradingView script'))
    }
    const cleanup = () => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    if (!existingScript) {
      script.id = TV_SCRIPT_ELEMENT_ID
      script.src = scriptUrl
      script.async = true
      script.fetchPriority = 'high'
      document.head.appendChild(script)
    }
  })
  return tvScriptPromise
}

// ---------------------------------------------------------------------------
// Iframe CSS patching utility
// ---------------------------------------------------------------------------

function patchTVAxisCanvasTheme(iframe: HTMLIFrameElement): void {
  const iframeWindow = iframe.contentWindow
  const doc = iframe.contentDocument
  if (!iframeWindow || !doc) return

  const context = doc.createElement('canvas').getContext('2d')
  const contextPrototype = context ? Object.getPrototypeOf(context) : null
  if (!contextPrototype || patchedAxisCanvasPrototypes.has(contextPrototype)) return

  const fillStyleDescriptor = Object.getOwnPropertyDescriptor(contextPrototype, 'fillStyle')
  if (!fillStyleDescriptor?.get || !fillStyleDescriptor.set) return

  const originalGetter = fillStyleDescriptor.get
  const originalSetter = fillStyleDescriptor.set

  Object.defineProperty(contextPrototype, 'fillStyle', {
    configurable: fillStyleDescriptor.configurable,
    enumerable: fillStyleDescriptor.enumerable,
    get: originalGetter,
    set(this: CanvasRenderingContext2D, value: string | CanvasGradient | CanvasPattern) {
      const isDarkTheme = doc.documentElement.classList.contains('orderx-theme-dark')
      const isAxisCanvas = Boolean(
        this.canvas.closest('.price-axis, .time-axis, .price-axis-container')
      )
      const isPriceAxisStub = Boolean(
        this.canvas.closest('.price-axis-container') && !this.canvas.closest('.price-axis')
      )
      const normalizedValue = typeof value === 'string' ? value.toLowerCase() : null

      let nextValue = value
      if (isDarkTheme && isAxisCanvas && normalizedValue === TV_LIGHT_AXIS_HIGHLIGHT) {
        nextValue = CHART_COLORS.darkAxisHighlight
      } else if (isDarkTheme && isPriceAxisStub && normalizedValue === TV_LIGHT_PRIMARY_TEXT) {
        nextValue = CHART_COLORS.darkScaleText
      }

      originalSetter.call(this, nextValue)
    },
  })
  patchedAxisCanvasPrototypes.add(contextPrototype)
}

const tvIframeThemes = new WeakMap<Document, { theme: TVColorTheme }>()

function applyTVThemeMarker(doc: Document, colorTheme: TVColorTheme): void {
  for (const el of [doc.documentElement, doc.body]) {
    if (!el) continue
    if (el.classList.contains('orderx-theme-light') !== (colorTheme === 'light')) {
      el.classList.toggle('orderx-theme-light', colorTheme === 'light')
    }
    if (el.classList.contains('orderx-theme-dark') !== (colorTheme === 'dark')) {
      el.classList.toggle('orderx-theme-dark', colorTheme === 'dark')
    }
  }
}

function patchTVIframeTheme(iframe: HTMLIFrameElement, colorTheme: TVColorTheme): void {
  const doc = iframe.contentDocument
  if (!doc) return

  applyTVThemeMarker(doc, colorTheme)
  // TV always runs its own 'light' theme and rewrites <html class> (layout load,
  // chart_theme_changed), wiping the marker tv-custom.css keys its dark palette on — the
  // chart then renders ink text on the dark pane. Re-assert it whenever TV touches it.
  const tracked = tvIframeThemes.get(doc)
  if (tracked) {
    tracked.theme = colorTheme
  } else {
    const entry = { theme: colorTheme }
    const observer = new MutationObserver(() => applyTVThemeMarker(doc, entry.theme))
    for (const el of [doc.documentElement, doc.body]) {
      if (el) observer.observe(el, { attributes: true, attributeFilter: ['class'] })
    }
    tvIframeThemes.set(doc, entry)
  }
  patchTVAxisCanvasTheme(iframe)

  // Belt-and-suspenders: inject CSS variables directly into the iframe document.
  // tv-custom.css (via custom_css_url) is the authoritative source for all rule-based
  // overrides (visibility, backgrounds, etc.). This injection only sets CSS vars because
  // some TV versions apply their own :root vars AFTER custom_css_url is parsed.
  // Do NOT duplicate hide/show rules here — keep tv-custom.css as the single source of truth.
  const styleId = 'orderx-tv-theme-overrides'
  const existingStyle = doc.getElementById(styleId)
  const style =
    existingStyle instanceof HTMLStyleElement ? existingStyle : doc.createElement('style')
  style.id = styleId
  // The page's live surface tokens (Dark/Blue/Light differ), so TV's own dialogs sit on the
  // same popover as the app's instead of a hard-coded near-black.
  const hostStyle = getComputedStyle(document.documentElement)
  const hostToken = (name: string) => hostStyle.getPropertyValue(name).trim()
  const hostTokens = [
    ['--orderx-host-popover', hostToken('--popover')],
    ['--orderx-host-foreground', hostToken('--popover-foreground') || hostToken('--foreground')],
    ['--orderx-host-emphasis', hostToken('--emphasis')],
    ['--orderx-host-emphasis-foreground', hostToken('--emphasis-foreground')],
  ]
    .filter(([, value]) => value && value !== 'initial')
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n      ')
  style.textContent = `
    :root {
      ${hostTokens}
      --tv-color-pane-background: transparent !important;
      --tv-color-platform-background: transparent !important;
      --color-pane-bg: transparent !important;
      --color-chart-page-bg: transparent !important;
      --tv-color-top-toolbar-background: transparent;
      --tv-color-left-toolbar-background: transparent;
      --tv-color-bottom-toolbar-background: transparent;
      --tv-color-item-active-bg: transparent;
      --tv-color-item-hover-bg: transparent;
      --tv-color-pane-hover-bg: transparent;
      --color-price-axis-highlight: ${
        colorTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(12, 12, 13, 0.08)'
      } !important;
      --color-price-axis-gear: ${
        colorTheme === 'dark' ? CHART_COLORS.darkScaleText : CHART_COLORS.scaleText
      } !important;
      --color-price-axis-gear-hover: ${colorTheme === 'dark' ? '#FFFFFF' : '#0C0C0D'} !important;
    }
    html,
    body,
    html.theme-dark,
    body.theme-dark,
    .theme-dark,
    .chart-container {
      background: transparent !important;
      background-color: transparent !important;
    }
  `
  if (!style.isConnected) doc.head.appendChild(style)

  const patchElements = () => {
    const currentDoc = iframe.contentDocument
    if (!currentDoc) return

    // Transparent backgrounds for price/time axis elements.
    // Targeted regex — avoids matching toolbar/drawing elements that should keep their own bg.
    const scalePattern =
      /price[-_]?axis|right[-_]?scale|scale[-_]?widget|price[-_]?scale[-_]?widget|y[-_]?axis|axis[-_]?container|scale[-_]?container|pane[-_]?controls|bottom[-_]?widget|chart[-_]?bottom[-_]?toolbar|main[-_]?series[-_]?scale[-_]?menu/i
    currentDoc.querySelectorAll<HTMLElement>('[class]').forEach((el) => {
      if (typeof el.className === 'string' && scalePattern.test(el.className)) {
        el.style.setProperty('background', 'transparent', 'important')
        el.style.setProperty('background-color', 'transparent', 'important')
      }
    })
  }

  patchElements()
  // TV may finish rendering scale elements slightly after onChartReady fires.
  // 500 ms is more conservative than 300 ms for low-end devices.
  setTimeout(patchElements, 500)
}

function formatLegendTimeframe(timeframeSeconds: number, tick: boolean): string {
  if (tick) return 'Tick'
  return getChartTimeframeInfo(timeframeSeconds)?.code ?? `${timeframeSeconds}s`
}

function patchTVLegendTimeframe(
  iframe: HTMLIFrameElement,
  timeframeSeconds: number,
  tick: boolean
): void {
  const intervalTitle = iframe.contentDocument?.querySelector(
    '[data-qa-id="title-wrapper legend-source-interval"]'
  )
  if (!intervalTitle) return

  const label = formatLegendTimeframe(timeframeSeconds, tick)
  const textNodeWalker = iframe.contentDocument?.createTreeWalker(
    intervalTitle,
    NodeFilter.SHOW_TEXT
  )
  let textNode = textNodeWalker?.nextNode()
  while (textNode && !textNode.textContent?.trim()) {
    textNode = textNodeWalker?.nextNode()
  }
  if (!textNode || textNode.textContent?.trim() === label) return

  textNode.textContent = textNode.textContent?.replace(textNode.textContent.trim(), label) ?? label
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseTVWidgetOptions {
  symbol: string
  /** Bar width the data is fetched at (Tick passes 1, with `tick` set). */
  timeframe: number
  /** Tick: open on TV's one-tick resolution, which the datafeed serves from the 1s bars. */
  tick?: boolean
  datafeedRef: React.RefObject<ChartDatafeed | null>
  savedData?: string
  colorTheme: TVColorTheme
  locale: AppLocale
  contextMenuItemsProcessor?: (
    items: readonly unknown[],
    actionsFactory: {
      createAction: (options: {
        actionId: string
        label: string
        onExecute?: () => void
      }) => unknown
      createSeparator?: () => unknown
    },
    params: { menuName: string; detail?: { type?: string } }
  ) => readonly unknown[] | Promise<readonly unknown[]>
  onTradingOrderIntent?: (intent: OrderXTradingOrderIntent) => void
  /**
   * Whether this chart's market can take an order. False dims and blocks the legend's broker
   * Buy/Sell buttons (tv-custom.css, `data-orderx-untradable`): the broker's isTradable doesn't
   * touch those buttons in this library version.
   */
  tradable?: boolean
  onIndicatorPickerClick?: () => void
  /** `/` on the chart: open (not toggle) our indicator panel instead of TV's own dialog. */
  onIndicatorPickerOpen?: () => void
  onCompareClick?: () => void
  onAddChartTabRequest?: () => void
  /** A plain letter typed while the chart has focus (TradingView's own type-to-search is off). */
  onTypeToSearch?: (key: string) => void
  isFullscreen?: boolean
  onFullscreenToggle?: () => void
  /** Warms the custom picker chunk once the native header is ready. */
  onIndicatorPickerPrefetch?: () => void

  onReady: (widget: TVWidgetInstance) => void
}

interface UseTVWidgetReturn {
  containerRef: React.RefObject<HTMLDivElement | null>
  widgetRef: React.RefObject<TVWidgetInstance | null>
  isChartReady: boolean
}

export function useTVWidget({
  symbol,
  timeframe,
  tick = false,
  datafeedRef,
  savedData,
  colorTheme,
  locale,
  contextMenuItemsProcessor,
  onTradingOrderIntent,
  tradable = true,
  onIndicatorPickerClick,
  onIndicatorPickerOpen,
  onCompareClick,
  onAddChartTabRequest,
  onTypeToSearch,
  isFullscreen = false,
  onFullscreenToggle,
  onIndicatorPickerPrefetch,

  onReady,
}: UseTVWidgetOptions): UseTVWidgetReturn {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<TVWidgetInstance | null>(null)
  const [isChartReady, setIsChartReady] = useState(false)

  const studyIdsRef = useRef<AnalyticsStudyIdMap>({})
  const symbolRef = useRef(symbol)
  symbolRef.current = symbol
  const timeframeRef = useRef(timeframe)
  timeframeRef.current = timeframe
  const colorThemeRef = useRef(colorTheme)
  colorThemeRef.current = colorTheme

  // Latest-ref pattern: always call the most recent onReady without re-running the init effect
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  const contextMenuItemsProcessorRef = useRef(contextMenuItemsProcessor)
  contextMenuItemsProcessorRef.current = contextMenuItemsProcessor
  const onTradingOrderIntentRef = useRef(onTradingOrderIntent)
  onTradingOrderIntentRef.current = onTradingOrderIntent
  const onIndicatorPickerClickRef = useRef(onIndicatorPickerClick)
  const onIndicatorPickerOpenRef = useRef(onIndicatorPickerOpen)
  onIndicatorPickerOpenRef.current = onIndicatorPickerOpen
  const onCompareClickRef = useRef(onCompareClick)
  const onAddChartTabRequestRef = useRef(onAddChartTabRequest)
  const onTypeToSearchRef = useRef(onTypeToSearch)
  const isFullscreenRef = useRef(isFullscreen)
  const onFullscreenToggleRef = useRef(onFullscreenToggle)
  const onIndicatorPickerPrefetchRef = useRef(onIndicatorPickerPrefetch)
  const fullscreenButtonRootRef = useRef<Root | null>(null)
  const brokerBridgeRef = useRef<OrderXBrokerBridge | null>(null)

  // Flag on the chart iframe's root; tv-custom.css dims and blocks the broker Buy/Sell buttons
  // under it. Re-applied once a (re)created widget is ready.
  useEffect(() => {
    if (!isChartReady) return
    const root = containerRef.current?.querySelector('iframe')?.contentDocument?.documentElement
    root?.toggleAttribute('data-orderx-untradable', !tradable)
  }, [isChartReady, tradable])

  useEffect(() => {
    onIndicatorPickerClickRef.current = onIndicatorPickerClick
  }, [onIndicatorPickerClick])

  useEffect(() => {
    onCompareClickRef.current = onCompareClick
  }, [onCompareClick])

  useEffect(() => {
    onAddChartTabRequestRef.current = onAddChartTabRequest
  }, [onAddChartTabRequest])

  useEffect(() => {
    onTypeToSearchRef.current = onTypeToSearch
  }, [onTypeToSearch])

  useEffect(() => {
    isFullscreenRef.current = isFullscreen
    const root = fullscreenButtonRootRef.current
    if (!root) return
    renderFullscreenHeaderButton(root, isFullscreen, () => onFullscreenToggleRef.current?.())
  }, [isFullscreen])

  useEffect(() => {
    onFullscreenToggleRef.current = onFullscreenToggle
  }, [onFullscreenToggle])

  useEffect(() => {
    onIndicatorPickerPrefetchRef.current = onIndicatorPickerPrefetch
  }, [onIndicatorPickerPrefetch])

  useEffect(() => {
    if (!containerRef.current) return
    let destroyed = false
    let unsubscribeThemeChange: (() => void) | null = null
    const headerButtonRoots: Root[] = []
    let headerLayoutFrame: number | null = null

    // Parse and sanitize persisted state while the external library is still
    // downloading/executing. Doing this inside ensureTVScript().then() made
    // these independent tasks a waterfall on a cold load.
    let parsedSavedData = null
    if (savedData) {
      try {
        parsedSavedData = sanitizeTVClientStyleState(JSON.parse(savedData), colorThemeRef.current)
      } catch (e) {
        console.error('[useTVWidget] Failed to parse saved chart content:', e)
      }
    }

    ensureTVScript()
      .then(() => {
        if (destroyed || !containerRef.current || !window.TradingView || !datafeedRef.current)
          return
        const datafeed = datafeedRef.current
        // Read once: like the locale, a separator change applies the next time the chart mounts.
        const separatorStyle = getNumberFormatter().style
        const separators = SEPARATOR_STYLES[separatorStyle]

        const widget = new window.TradingView.widget({
          container: containerRef.current,
          datafeed,
          symbol,
          interval: chartResolution(timeframe, tick),
          autosize: true,
          // Keep this fixed: dark styling is applied by patchTVIframeTheme so the main pane
          // can remain transparent. Passing the app theme here makes TradingView opaque.
          theme: 'light',
          locale: resolveTVLocale(locale),
          library_path: process.env.NEXT_PUBLIC_TV_LIBRARY_PATH ?? '/charting_library/',
          custom_css_url: `${window.location.origin}/style/tv-custom.css?v=20261019-ox15`,
          saved_data: parsedSavedData,
          favorites: {
            intervals: ['5', '60', '1'],
          },
          header_widget_buttons_mode: 'fullsize',
          studies_access: {
            type: 'white',
            tools: NATIVE_STUDIES_ACCESS_NAMES.map((name) => ({ name })),
          },
          numeric_formatting: {
            decimal_sign: separators.decimal,
            grouping_separator: separators.group,
          },
          custom_formatters: {
            studyFormatterFactory: (format: { type: string; precision?: number }) => {
              if (format.type === 'percent') {
                return {
                  format: (value: number) =>
                    localizeSeparators(
                      formatPercentValue(value, format.precision ?? 4),
                      separatorStyle
                    ),
                }
              }

              if (format.type !== 'volume') return null

              return {
                format: (value: number) =>
                  localizeSeparators(
                    formatCompactVolumeValue(value, format.precision ?? 4),
                    separatorStyle
                  ),
              }
            },
          },
          broker_factory: (host: unknown) => {
            const bridge = new OrderXBrokerBridge(
              host as ConstructorParameters<typeof OrderXBrokerBridge>[0],
              {
                onOrderIntent: (intent) => onTradingOrderIntentRef.current?.(intent),
              }
            )
            brokerBridgeRef.current = bridge
            return bridge
          },
          broker_config: {
            configFlags: {
              supportOrdersHistory: false,
              supportPositions: false,
              supportExecutions: false,
            },
            customUI: {
              showOrderDialog: async (order: unknown) => {
                onTradingOrderIntentRef.current?.({
                  order: order as OrderXTradingOrderIntent['order'],
                  source: 'order-dialog',
                })
                return false
              },
            },
          },
          loading_screen: {
            backgroundColor: TV_TRANSPARENT_BACKGROUND,
          },
          custom_indicators_getter: () =>
            Promise.resolve([
              ...createVisibleRangeProfileControlStudies(),
              ...createLevelProfileControlStudies(),
              ...createOrderbookHeatmapControlStudies(),
              ...CHART_ANALYTICS_DEFINITIONS.filter(
                (definition) =>
                  definition.key !== 'buySellLiq' && definition.key !== 'netBuyTradeCount'
              ).map((definition) => {
                const studyName = `OrderX ${definition.label}`
                const isZeroCenteredHistogram =
                  definition.recommendedVisual === 'zero-centered-histogram'
                const isHistogram =
                  definition.recommendedVisual === 'histogram' || isZeroCenteredHistogram
                const isDots = definition.recommendedVisual === 'dots'
                const isStepLine = definition.recommendedVisual === 'step-line'
                const isCandles = definition.recommendedVisual === 'candles'
                const isCvd = definition.key === 'cumulativeVolumeDelta'
                const isTotalTradeCount = definition.key === 'totalTradeCount'
                const isMainPaneVolume =
                  definition.key === 'volumeBaseBlockIncl' ||
                  definition.key === 'buyVolumeBaseBlockIncl' ||
                  definition.key === 'sellVolumeBaseBlockIncl'
                const isIndependentScaleOverlay =
                  definition.key === 'parkinsonVolatility' || isMainPaneVolume
                const studyInputSpecs = getEffectiveStudyInputs(definition)
                const studyInputs = buildStudyInputs(studyInputSpecs)

                return {
                  name: studyName,
                  metainfo: {
                    _metainfoVersion: 53,
                    id: `OrderX${definition.key}@tv-basicstudies-1`,
                    description: studyName,
                    shortDescription: definition.shortLabel,
                    is_price_study: definition.placement === 'overlay',
                    isCustomIndicator: true,
                    linkedToSeries:
                      definition.placement === 'overlay' && !isIndependentScaleOverlay,
                    priceScale:
                      definition.key === 'parkinsonVolatility'
                        ? 1
                        : isMainPaneVolume
                          ? 2
                          : undefined,
                    format: {
                      type:
                        definition.key === 'deltaPercentage' ||
                        definition.key === 'parkinsonVolatility' ||
                        isFundingRateMetric(definition.key)
                          ? 'percent'
                          : definition.unit === 'price'
                            ? 'price'
                            : 'volume',
                      precision: getStudyPrecision(definition.key, definition.unit),
                    },
                    plots: isMainPaneVolume
                      ? [
                          { id: 'plot_0', type: 'line' },
                          { id: 'plot_scale_upper', type: 'line' },
                        ]
                      : isTotalTradeCount
                        ? [
                            { id: 'plot_line', type: 'line' },
                            { id: 'plot_bars', type: 'line' },
                            { id: 'plot_value', type: 'line' },
                          ]
                        : isCvd
                          ? [
                              { id: 'plot_0', type: 'line' },
                              { id: 'plot_value', type: 'line' },
                              { id: 'plot_open', type: 'ohlc_open', target: 'ohlc' },
                              { id: 'plot_high', type: 'ohlc_high', target: 'ohlc' },
                              { id: 'plot_low', type: 'ohlc_low', target: 'ohlc' },
                              { id: 'plot_close', type: 'ohlc_close', target: 'ohlc' },
                              {
                                id: 'plot_colorer',
                                type: 'ohlc_colorer',
                                target: 'ohlc',
                                palette: 'palette_0',
                              },
                              {
                                id: 'plot_wick_colorer',
                                type: 'wick_colorer',
                                target: 'ohlc',
                                palette: 'palette_0',
                              },
                            ]
                          : isCandles
                            ? [
                                { id: 'plot_open', type: 'ohlc_open', target: 'ohlc' },
                                { id: 'plot_high', type: 'ohlc_high', target: 'ohlc' },
                                { id: 'plot_low', type: 'ohlc_low', target: 'ohlc' },
                                { id: 'plot_close', type: 'ohlc_close', target: 'ohlc' },
                                {
                                  id: 'plot_colorer',
                                  type: 'ohlc_colorer',
                                  target: 'ohlc',
                                  palette: 'palette_0',
                                },
                                {
                                  id: 'plot_wick_colorer',
                                  type: 'wick_colorer',
                                  target: 'ohlc',
                                  palette: 'palette_0',
                                },
                                { id: 'plot_scale_upper', type: 'line' },
                                { id: 'plot_scale_lower', type: 'line' },
                              ]
                            : isZeroCenteredHistogram
                              ? [
                                  { id: 'plot_0', type: 'line' },
                                  {
                                    id: 'palette_0',
                                    type: 'colorer',
                                    target: 'plot_0',
                                    palette: 'palette_0',
                                  },
                                ]
                              : [{ id: 'plot_0', type: 'line' }],
                    ...(isCandles || isCvd
                      ? { ohlcPlots: { ohlc: { title: definition.shortLabel } } }
                      : {}),
                    ...(isCandles || isCvd || isZeroCenteredHistogram
                      ? {
                          palettes: {
                            palette_0: {
                              colors:
                                isCandles || isCvd
                                  ? {
                                      0: { name: 'Down' },
                                      1: { name: 'Up' },
                                    }
                                  : {
                                      0: { name: 'Negative' },
                                      1: { name: 'Positive' },
                                    },
                              valToIndex: {
                                0: 0,
                                1: 1,
                              },
                            },
                          },
                        }
                      : {}),
                    defaults: {
                      styles: isMainPaneVolume
                        ? {
                            plot_0: {
                              linestyle: 0,
                              linewidth: 4,
                              plottype: 5,
                              trackPrice: false,
                              transparency: 0,
                              visible: true,
                              color: CHART_ANALYTICS_BY_KEY[definition.key].color,
                              display: 15,
                            },
                            plot_scale_upper: {
                              linestyle: 0,
                              linewidth: 1,
                              plottype: 0,
                              trackPrice: false,
                              transparency: 100,
                              visible: true,
                              color: TV_TRANSPARENT_BACKGROUND,
                              // Keep the transparent plot in pane autoscale calculations without
                              // exposing its helper value in the legend, price scale, or data window.
                              display: 1,
                            },
                          }
                        : isTotalTradeCount
                          ? {
                              plot_line: {
                                linestyle: 0,
                                linewidth: 2,
                                plottype: 0,
                                trackPrice: false,
                                transparency: 0,
                                visible: true,
                                color: CHART_ANALYTICS_BY_KEY[definition.key].color,
                                display: 7,
                              },
                              plot_bars: {
                                linestyle: 0,
                                linewidth: 4,
                                plottype: 5,
                                trackPrice: false,
                                transparency: 0,
                                visible: true,
                                color: CHART_ANALYTICS_BY_KEY[definition.key].color,
                                display: 7,
                              },
                              plot_value: {
                                linestyle: 0,
                                linewidth: 1,
                                plottype: 0,
                                trackPrice: false,
                                transparency: 0,
                                visible: true,
                                color: CHART_ANALYTICS_BY_KEY[definition.key].color,
                                display: 8,
                              },
                            }
                          : isCandles
                            ? {
                                plot_scale_upper: {
                                  linestyle: 0,
                                  linewidth: 1,
                                  plottype: 0,
                                  trackPrice: false,
                                  transparency: 100,
                                  visible: true,
                                  color: TV_TRANSPARENT_BACKGROUND,
                                  display: 0,
                                },
                                plot_scale_lower: {
                                  linestyle: 0,
                                  linewidth: 1,
                                  plottype: 0,
                                  trackPrice: false,
                                  transparency: 100,
                                  visible: true,
                                  color: TV_TRANSPARENT_BACKGROUND,
                                  display: 0,
                                },
                              }
                            : {
                                plot_0: {
                                  linestyle: definition.recommendedVisual === 'dashed-line' ? 2 : 0,
                                  linewidth: isHistogram ? 4 : isDots ? 3 : 2,
                                  plottype: isHistogram ? 5 : isStepLine ? 9 : isDots ? 2 : 0,
                                  trackPrice: false,
                                  transparency: 0,
                                  visible: true,
                                  color: CHART_ANALYTICS_BY_KEY[definition.key].color,
                                  display: isCvd ? 7 : 15,
                                },
                                ...(isCvd
                                  ? {
                                      plot_value: {
                                        linestyle: 0,
                                        linewidth: 1,
                                        plottype: 0,
                                        trackPrice: false,
                                        transparency: 0,
                                        visible: true,
                                        color: CHART_ANALYTICS_BY_KEY[definition.key].color,
                                        display: 8,
                                      },
                                    }
                                  : {}),
                              },
                      ohlcPlots:
                        isCandles || isCvd
                          ? {
                              ohlc: {
                                plottype: 'ohlc_candles',
                                color: ZERO_CENTERED_POSITIVE_COLOR,
                                drawWick: true,
                                drawBorder: false,
                                wickColor: CHART_COLORS.neutral,
                                borderColor: TV_TRANSPARENT_BACKGROUND,
                                display: isCvd ? 7 : 15,
                                visible: true,
                              },
                            }
                          : undefined,
                      palettes:
                        isCandles || isCvd || isZeroCenteredHistogram
                          ? {
                              palette_0: {
                                colors: {
                                  0: {
                                    color: ZERO_CENTERED_NEGATIVE_COLOR,
                                    width: 1,
                                    style: 0,
                                  },
                                  1: {
                                    color: ZERO_CENTERED_POSITIVE_COLOR,
                                    width: 1,
                                    style: 0,
                                  },
                                },
                              },
                            }
                          : {},
                      precision: getStudyPrecision(definition.key, definition.unit),
                      inputs: studyInputs.defaults,
                    },
                    styles: isMainPaneVolume
                      ? {
                          plot_0: {
                            title: definition.shortLabel,
                            histogramBase: 0,
                          },
                          plot_scale_upper: { title: '' },
                        }
                      : isTotalTradeCount
                        ? {
                            plot_line: { title: 'Total Trade Count (Line)' },
                            plot_bars: {
                              title: 'Total Trade Count (Bars)',
                              histogramBase: 0,
                            },
                            plot_value: { title: definition.shortLabel, isHidden: true },
                          }
                        : isCandles
                          ? {
                              plot_scale_upper: {
                                title: '',
                              },
                              plot_scale_lower: {
                                title: '',
                              },
                            }
                          : {
                              plot_0: {
                                title: definition.shortLabel,
                                ...(isHistogram ? { histogramBase: 0 } : {}),
                                ...(isDots ? { joinPoints: false } : {}),
                              },
                              ...(isCvd
                                ? {
                                    plot_value: {
                                      title: definition.shortLabel,
                                      isHidden: true,
                                    },
                                  }
                                : {}),
                            },
                    inputs: studyInputs.inputs,
                  },
                  constructor: function (this: {
                    _context?: {
                      symbol: {
                        time: number
                      }
                    }
                    init?: (context: {
                      symbol: {
                        time: number
                      }
                    }) => void
                    main?: (
                      context: {
                        symbol: {
                          time: number
                        }
                      },
                      inputCallback: (index: number) => string | number | boolean | undefined
                    ) => number[]
                  }) {
                    this.init = function (context) {
                      this._context = context
                    }

                    this.main = function (context, inputCallback) {
                      this._context = context

                      const currentBarTimeSec = normalizeBarTimeSec(Number(context.symbol.time))
                      const inputValues = readStudyInputValues(studyInputSpecs, inputCallback)
                      const measure: CvdMeasure = inputValues.measure === 'quote' ? 'quote' : 'base'
                      const dataContext = {
                        symbol: symbolRef.current,
                        timeframeSeconds: timeframeRef.current,
                      }

                      if (isMainPaneVolume) {
                        const value =
                          datafeedRef.current?.getAnalyticsValue(
                            definition.key,
                            currentBarTimeSec,
                            dataContext,
                            { measure, inputs: inputValues }
                          ) ?? NaN
                        return [
                          value,
                          Number.isFinite(value) ? getMainPaneVolumeScaleCeiling(value) : NaN,
                        ]
                      }

                      if (isCvd) {
                        const valueOptions = { measure, inputs: inputValues }
                        const close =
                          datafeedRef.current?.getAnalyticsValue(
                            definition.key,
                            currentBarTimeSec,
                            dataContext,
                            valueOptions
                          ) ?? NaN
                        if (inputValues.visualStyle !== 'candles') {
                          return [close, close, NaN, NaN, NaN, NaN, NaN, NaN]
                        }
                        const previous =
                          datafeedRef.current?.getPreviousAnalyticsValue(
                            definition.key,
                            currentBarTimeSec,
                            dataContext,
                            valueOptions
                          ) ?? 0
                        const colorIndex = close >= previous ? 1 : 0
                        return [
                          NaN,
                          close,
                          previous,
                          Math.max(previous, close),
                          Math.min(previous, close),
                          close,
                          colorIndex,
                          colorIndex,
                        ]
                      }

                      if (isTotalTradeCount) {
                        const value =
                          datafeedRef.current?.getAnalyticsValue(
                            definition.key,
                            currentBarTimeSec,
                            dataContext,
                            { measure, inputs: inputValues }
                          ) ?? NaN
                        return inputValues.visualStyle === 'bars'
                          ? [NaN, value, value]
                          : [value, NaN, value]
                      }

                      if (isCandles) {
                        const fields =
                          definition.key === 'openInterestBase' && measure === 'quote'
                            ? OI_OHLC_FIELDS.openInterestQuote
                            : OI_OHLC_FIELDS[definition.key]
                        if (!fields) {
                          return [NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN]
                        }

                        const read = (field: string) =>
                          datafeedRef.current?.getRawAnalyticsValue(
                            field,
                            currentBarTimeSec,
                            dataContext
                          ) ?? NaN

                        const open = read(fields.open)
                        const high = read(fields.high)
                        const low = read(fields.low)
                        const close = read(fields.close)

                        if (![open, high, low, close].every(Number.isFinite)) {
                          return [NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN]
                        }

                        const colorIndex = close >= open ? 1 : 0
                        const scaleBounds = getOpenInterestScaleBounds(open, high, low, close)
                        return [
                          open,
                          high,
                          low,
                          close,
                          colorIndex,
                          colorIndex,
                          scaleBounds.upper,
                          scaleBounds.lower,
                        ]
                      }

                      const rawValue = datafeedRef.current?.getAnalyticsValue(
                        definition.key,
                        currentBarTimeSec,
                        dataContext,
                        studyInputSpecs.length ? { measure, inputs: inputValues } : undefined
                      )

                      const value = normalizeAnalyticsStudyValue(definition.key, rawValue)

                      if (isZeroCenteredHistogram) {
                        const numericValue = value ?? NaN
                        const paletteIndex =
                          Number.isFinite(numericValue) && numericValue >= 0 ? 1 : 0
                        return [numericValue, paletteIndex]
                      }

                      return [value ?? NaN]
                    }
                  },
                }
              }),
              {
                name: 'OrderX Liquidation Split',
                metainfo: {
                  _metainfoVersion: 53,
                  id: 'OrderXLiquidationSplit@tv-basicstudies-1',
                  description: 'OrderX Liquidation Split',
                  shortDescription: 'Liquidations (Buy/Sell)',
                  is_price_study: false,
                  isCustomIndicator: true,
                  linkedToSeries: false,
                  format: {
                    type: 'volume',
                    precision: 4,
                  },
                  plots: [
                    { id: 'plot_buy', type: 'line' },
                    { id: 'plot_sell', type: 'line' },
                  ],
                  defaults: {
                    styles: {
                      plot_buy: {
                        linestyle: 0,
                        linewidth: 4,
                        plottype: 5,
                        trackPrice: false,
                        transparency: 0,
                        visible: true,
                        color: LIQUIDATION_BUY_COLOR,
                      },
                      plot_sell: {
                        linestyle: 0,
                        linewidth: 4,
                        plottype: 5,
                        trackPrice: false,
                        transparency: 0,
                        visible: true,
                        color: LIQUIDATION_SELL_COLOR,
                      },
                    },
                    precision: 4,
                    inputs: LIQUIDATION_SPLIT_STUDY_INPUTS.defaults,
                  },
                  styles: {
                    plot_buy: {
                      title: 'Liquidations (Buy)',
                      histogramBase: 0,
                    },
                    plot_sell: {
                      title: 'Liquidations (Sell)',
                      histogramBase: 0,
                    },
                  },
                  inputs: LIQUIDATION_SPLIT_STUDY_INPUTS.inputs,
                },
                constructor: function (this: {
                  _context?: {
                    symbol: {
                      time: number
                    }
                  }
                  init?: (context: {
                    symbol: {
                      time: number
                    }
                  }) => void
                  main?: (
                    context: {
                      symbol: {
                        time: number
                      }
                    },
                    inputCallback: (index: number) => string | number | boolean | undefined
                  ) => number[]
                }) {
                  this.init = function (context) {
                    this._context = context
                  }

                  this.main = function (context, inputCallback) {
                    this._context = context

                    const currentBarTimeSec = normalizeBarTimeSec(Number(context.symbol.time))
                    const inputValues = readStudyInputValues(
                      LIQUIDATION_SPLIT_INPUT_SPECS,
                      inputCallback
                    )
                    const measure: CvdMeasure = inputValues.measure === 'quote' ? 'quote' : 'base'
                    const dataContext = {
                      symbol: symbolRef.current,
                      timeframeSeconds: timeframeRef.current,
                    }
                    const buyValue =
                      datafeedRef.current?.getLiquidationSideValue(
                        'buy',
                        currentBarTimeSec,
                        dataContext,
                        measure
                      ) ?? NaN
                    const sellValue =
                      datafeedRef.current?.getLiquidationSideValue(
                        'sell',
                        currentBarTimeSec,
                        dataContext,
                        measure
                      ) ?? NaN

                    return [buyValue, Number.isFinite(sellValue) ? -Math.abs(sellValue) : NaN]
                  }
                },
              },
              {
                name: SPECIAL_ANALYTICS_STUDY_NAMES.netBuyTradeCount,
                metainfo: {
                  _metainfoVersion: 53,
                  id: 'OrderXTradeCountBuySell@tv-basicstudies-1',
                  description: SPECIAL_ANALYTICS_STUDY_NAMES.netBuyTradeCount,
                  shortDescription: 'Trade Count (Buy/Sell)',
                  is_price_study: false,
                  isCustomIndicator: true,
                  linkedToSeries: false,
                  format: { type: 'volume', precision: 0 },
                  plots: [
                    { id: 'plot_buy', type: 'line' },
                    { id: 'plot_sell', type: 'line' },
                  ],
                  defaults: {
                    styles: {
                      plot_buy: {
                        linestyle: 0,
                        linewidth: 4,
                        plottype: 5,
                        trackPrice: false,
                        transparency: 0,
                        visible: true,
                        color: ZERO_CENTERED_POSITIVE_COLOR,
                      },
                      plot_sell: {
                        linestyle: 0,
                        linewidth: 4,
                        plottype: 5,
                        trackPrice: false,
                        transparency: 0,
                        visible: true,
                        color: ZERO_CENTERED_NEGATIVE_COLOR,
                      },
                    },
                    precision: 0,
                    inputs: {},
                  },
                  styles: {
                    plot_buy: { title: 'Trades (Buy)', histogramBase: 0 },
                    plot_sell: { title: 'Trades (Sell)', histogramBase: 0 },
                  },
                  inputs: [],
                },
                constructor: function (this: {
                  _context?: { symbol: { time: number } }
                  init?: (context: { symbol: { time: number } }) => void
                  main?: (context: { symbol: { time: number } }) => number[]
                }) {
                  this.init = function (context) {
                    this._context = context
                  }

                  this.main = function (context) {
                    this._context = context
                    const currentBarTimeSec = normalizeBarTimeSec(Number(context.symbol.time))
                    const dataContext = {
                      symbol: symbolRef.current,
                      timeframeSeconds: timeframeRef.current,
                    }
                    const buyValue =
                      datafeedRef.current?.getAnalyticsValue(
                        'buyTradeCount',
                        currentBarTimeSec,
                        dataContext
                      ) ?? NaN
                    const sellValue =
                      datafeedRef.current?.getAnalyticsValue(
                        'sellTradeCount',
                        currentBarTimeSec,
                        dataContext
                      ) ?? NaN
                    return [buyValue, Number.isFinite(sellValue) ? -Math.abs(sellValue) : NaN]
                  }
                },
              },
            ]),
          context_menu: contextMenuItemsProcessor
            ? {
                items_processor: (
                  items: readonly unknown[],
                  actionsFactory: {
                    createAction: (options: {
                      actionId: string
                      label: string
                      onExecute?: () => void
                    }) => unknown
                    createSeparator?: () => unknown
                  },
                  params: { menuName: string; detail?: { type?: string } }
                ) =>
                  // Defer one tick so onPlusClick fires before we read lastPlusClickRef
                  new Promise<readonly unknown[]>((resolve) => {
                    setTimeout(() => {
                      resolve(
                        contextMenuItemsProcessorRef.current?.(items, actionsFactory, params) ??
                          items
                      )
                    }, 0)
                  }),
              }
            : undefined,

          disabled_features: [
            // Keep the native header enabled for resolutions and chart type.
            // Indicators, compare, and quick search are replaced with OrderX controls below.
            // 'control_bar',
            // The bottom toolbar (range presets, go-to-date, clock, scale) is our own
            // ChartToolbar, under the iframe.
            'timeframes_toolbar',
            // TV's own Go to dialog (Alt+G) throws inside gotoTime for a date before the
            // loaded history; ChartToolbar's Go to replaces it.
            'go_to_date',
            'use_localstorage_for_settings',
            'inactivity_gaps', // collapse periods without trading instead of showing empty slots
            'popup_hints',
            'header_symbol_search', // far-left Add symbol (+) button
            'symbol_search_hot_key',
            'header_quick_search', // replaced with OrderX "Add tab" below
            // 'symbol_info',
            'right_toolbar',
            'show_object_tree',
            'hide_right_toolbar_tabs',
            'trading_account_manager',
            'open_account_manager',
            'header_compare',
            'header_undo_redo',
            'header_settings',
            'header_saveload',
            'header_screenshot',
            'header_layouttoggle',
            'header_fullscreen_button', // replaced with the app-level FullscreenOverlay toggle
            'support_multicharts',
            'header_indicators',
            'loading_screen',
            'drawing_templates', // prevents 403 calls to cloud drawing_templates endpoint
          ],
          // seconds_resolution: without it TV drops the 1S/15S resolutions from its picker.
          enabled_features: [
            'buy_sell_buttons',
            'items_favoriting',
            'seconds_resolution',
            // Tick (1T) — see CHART_TICK_TIMEFRAME.
            'tick_resolution',
          ],
          trading_customization: TV_TRADING_CUSTOMIZATION,
          settings_overrides: TV_BACKGROUND_OVERRIDES,
          studies_overrides: {},
          overrides: {
            ...getTVChartOverrides(colorThemeRef.current),
            'paneProperties.legendProperties.showBackground': false,
            'paneProperties.legendProperties.backgroundTransparency': 100,
            'paneProperties.legendProperties.showStudyArguments': false,
          },
        })

        // TV builds its toolbar and legend while its iframe document is still loading, about a
        // second before onChartReady. Until that document carries the orderx-theme-* marker,
        // tv-custom.css falls back to TV's light palette: ink text that flips to white once the
        // marker lands. The widget inserts its iframe synchronously (as about:blank, then its
        // blob document), so poll for the real document and mark it as soon as its <head>
        // parses; headerReady is the backstop.
        const markTVIframe = () => {
          const iframe = containerRef.current?.querySelector('iframe')
          const doc = iframe?.contentDocument
          if (destroyed || !iframe || !doc?.head || doc.URL === 'about:blank') return false
          patchTVIframeTheme(iframe, colorThemeRef.current)
          return true
        }
        const markDeadline = performance.now() + 10_000
        const pollTVIframe = () => {
          if (!destroyed && !markTVIframe() && performance.now() < markDeadline) {
            setTimeout(pollTVIframe, 0)
          }
        }
        pollTVIframe()

        void widget
          .headerReady()
          .then(() => {
            if (destroyed) return
            markTVIframe()
            onIndicatorPickerPrefetchRef.current?.()

            const createOrderXHeaderButton = ({
              align,
              kind,
              label,
              iconOnly = false,
              onClick,
              children,
            }: {
              align: 'left' | 'right'
              kind: 'indicators' | 'compare'
              label: string
              iconOnly?: boolean
              onClick: () => void
              children: ReactNode
            }) => {
              const slot = widget.createButton({ align, useTradingViewStyle: false })
              slot.classList.add(
                'orderx-tv-header-button-slot',
                `orderx-tv-header-button-slot--${kind}`
              )
              const root = createRoot(slot)
              root.render(
                createElement(
                  'button',
                  {
                    type: 'button',
                    className: `orderx-tv-header-button${iconOnly ? ' orderx-tv-header-button--icon-only' : ''}`,
                    title: label,
                    'aria-label': label,
                    onClick,
                  },
                  children
                )
              )
              headerButtonRoots.push(root)
              return slot
            }

            const indicatorsSlot = createOrderXHeaderButton({
              align: 'left',
              kind: 'indicators',
              label: 'Indicators',
              onClick: () => onIndicatorPickerClickRef.current?.(),
              children: [
                createElement(SquareFunctionIcon, {
                  key: 'icon',
                  size: 16,
                  'aria-hidden': true,
                }),
                createElement('span', { key: 'label' }, 'Indicators'),
              ],
            })
            createOrderXHeaderButton({
              align: 'left',
              kind: 'compare',
              label: 'Compare',
              onClick: () => onCompareClickRef.current?.(),
              children: createElement('span', null, 'Compare'),
            })
            const fullscreenSlot = widget.createButton({
              align: 'right',
              useTradingViewStyle: false,
            })
            fullscreenSlot.classList.add(
              'orderx-tv-header-button-slot',
              'orderx-tv-header-button-slot--fullscreen'
            )
            const fullscreenRoot = createRoot(fullscreenSlot)
            fullscreenButtonRootRef.current = fullscreenRoot
            renderFullscreenHeaderButton(fullscreenRoot, isFullscreenRef.current, () =>
              onFullscreenToggleRef.current?.()
            )
            headerButtonRoots.push(fullscreenRoot)

            headerLayoutFrame = requestAnimationFrame(() => {
              if (destroyed) return
              const toolbar =
                fullscreenSlot.closest<HTMLElement>('[role="toolbar"]') ??
                fullscreenSlot.closest<HTMLElement>('[class*="topToolbar-"]')

              // TradingView wraps custom left controls with generated separator nodes.
              // Hide only the two dividers touching Indicators; other toolbar groups keep theirs.
              const indicatorRect = indicatorsSlot.getBoundingClientRect()
              const separatorScope = toolbar ?? indicatorsSlot.parentElement
              separatorScope
                ?.querySelectorAll<HTMLElement>('[class*="separator-"], [class*="separatorWrap-"]')
                .forEach((separator) => {
                  const rect = separator.getBoundingClientRect()
                  const touchesLeft = Math.abs(rect.right - indicatorRect.left) <= 12
                  const touchesRight = Math.abs(rect.left - indicatorRect.right) <= 12
                  if (touchesLeft || touchesRight) separator.style.display = 'none'
                })
            })
          })
          .catch(() => {})

        widget.onChartReady(() => {
          if (destroyed) {
            widget.remove()
            return
          }
          widget.setBuySellButtonsVisible = (visible: boolean) => {
            brokerBridgeRef.current?.setBuySellButtonsVisible(visible)
          }
          widgetRef.current = widget
          setIsChartReady(true)

          const chart = widget.activeChart()
          widget.navigationButtonsVisibility?.().setValue('visibleOnMouseOver')

          studyIdsRef.current = reconcileAnalyticsStudyIds(chart)

          // Re-apply chart overrides after saved_data is loaded.
          // saved_data can restore previously set background/main-series colors,
          // so we enforce our theme here to override whatever was persisted.
          try {
            applyTVClientStyle(widget, colorThemeRef.current)
          } catch {}

          // Re-apply theme colors whenever TV resets them (chart template load, etc.)
          const reapplyChartTheme = () => {
            if (destroyed) return
            try {
              applyTVClientStyle(widget, colorThemeRef.current)
            } catch {}
          }
          try {
            widget.subscribe('chart_theme_changed', reapplyChartTheme)
            unsubscribeThemeChange = () => {
              try {
                widget.unsubscribe('chart_theme_changed', reapplyChartTheme)
              } catch {}
            }
          } catch {}

          onReadyRef.current(widget)

          setTimeout(() => {
            const iframe = containerRef.current?.querySelector('iframe')
            if (iframe?.contentDocument) {
              patchTVIframeTheme(iframe, colorThemeRef.current)
            }
            if (!destroyed) warmUpPastHistory(widget, timeframeRef.current)
          }, 300)

          // saved_data may finish restoring main-series/background colors after onChartReady.
          // Re-apply once more after a short delay to be certain.
          setTimeout(() => {
            if (destroyed) return
            try {
              applyTVClientStyle(widget, colorThemeRef.current)
            } catch {}
          }, 1000)
        })
      })
      .catch(() => {})

    return () => {
      destroyed = true
      fullscreenButtonRootRef.current = null
      unsubscribeThemeChange?.()
      unsubscribeThemeChange = null
      if (headerLayoutFrame !== null) cancelAnimationFrame(headerLayoutFrame)
      // Deferred, not synchronous: this cleanup can run while React is still
      // committing an ancestor update (e.g. switching chart tabs unmounts this
      // widget and mounts another in the same commit). Synchronously unmounting
      // a nested `createRoot` root from inside that commit trips React's
      // "Attempted to synchronously unmount a root while React was already
      // rendering" error — queuing it lets the in-flight commit finish first.
      headerButtonRoots.forEach((root) => {
        queueMicrotask(() => root.unmount())
      })
      studyIdsRef.current = {}
      brokerBridgeRef.current = null
      if (widgetRef.current) {
        try {
          widgetRef.current.chart().removeAllShapes?.()
        } catch {}
        widgetRef.current.remove()
        widgetRef.current = null
      }
      setIsChartReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isChartReady) return
    const container = containerRef.current
    if (!container) return

    // Grid resizing changes the iframe viewport without resizing the host window.
    // Notify the chart after layout so its canvas follows the new viewport in Safari too.
    let frame: number | null = null
    const observer = new ResizeObserver(() => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = null
        const iframeWindow = container.querySelector('iframe')?.contentWindow
        iframeWindow?.dispatchEvent(new Event('resize'))
      })
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [isChartReady])

  useEffect(() => {
    if (!isChartReady) return
    const iframeWindow = containerRef.current?.querySelector('iframe')?.contentWindow
    if (!iframeWindow) return

    const handleChartTabSearchShortcut = (event: KeyboardEvent) => {
      // TradingView-style type-to-search: a bare letter on the chart opens the tab's instrument
      // picker with it already typed. Letters only, so digits keep TV's interval dialog; and never
      // while typing into one of TV's own fields (indicator search, drawing text, ...).
      if (
        /^[a-z]$/i.test(event.key) &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.repeat &&
        !isEditableTarget(event.target)
      ) {
        if (!onTypeToSearchRef.current) return
        event.preventDefault()
        event.stopImmediatePropagation()
        onTypeToSearchRef.current(event.key)
        return
      }

      // `/` is TV's indicators hotkey; route it to our panel, as the header button does.
      if (
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey &&
        !isEditableTarget(event.target) &&
        onIndicatorPickerOpenRef.current
      ) {
        event.preventDefault()
        event.stopImmediatePropagation()
        onIndicatorPickerOpenRef.current()
        return
      }

      const isCommandK =
        (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k'
      if (!isCommandK) return

      event.preventDefault()
      event.stopImmediatePropagation()

      if (event.shiftKey) {
        onAddChartTabRequestRef.current?.()
        return
      }

      // Keyboard events do not bubble out of the TradingView iframe. Forward
      // plain Cmd/Ctrl+K so the app-level command palette (DashboardCommandPalette)
      // still opens while focus is inside the chart.
      window.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'k',
          code: 'KeyK',
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          bubbles: true,
          cancelable: true,
        })
      )
    }

    iframeWindow.addEventListener('keydown', handleChartTabSearchShortcut, true)
    return () => iframeWindow.removeEventListener('keydown', handleChartTabSearchShortcut, true)
  }, [isChartReady])

  useEffect(() => {
    const widget = widgetRef.current
    const iframe = containerRef.current?.querySelector('iframe')
    if (!widget || !iframe?.contentDocument) return

    patchTVIframeTheme(iframe, colorTheme)
    try {
      applyTVClientStyle(widget, colorTheme)
    } catch {}

    // Dark and Blue are both TV 'dark'; a tint switch only changes the host tokens.
    const observer = new MutationObserver(() => patchTVIframeTheme(iframe, colorThemeRef.current))
    observer.observe(document.documentElement, { attributeFilter: ['data-page-tint'] })
    return () => observer.disconnect()
  }, [colorTheme])

  useEffect(() => {
    if (!isChartReady) return
    const iframe = containerRef.current?.querySelector('iframe')
    if (!iframe?.contentDocument) return

    // TradingView has no public interval formatter for the main-series legend.
    // Its iframe can render the new resolution a few frames after setResolution,
    // so re-apply the display-only label at a few bounded checkpoints.
    const apply = () => patchTVLegendTimeframe(iframe, timeframe, tick)
    apply()
    const timeoutIds = [100, 300, 750].map((delay) => setTimeout(apply, delay))
    return () => timeoutIds.forEach(clearTimeout)
  }, [isChartReady, timeframe, tick])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const chart = widgetRef.current.activeChart()
    studyIdsRef.current = reconcileAnalyticsStudyIds(chart)
  }, [isChartReady])

  return { containerRef, widgetRef, isChartReady }
}
